import { canonicalSubstanceKey } from "@/domain/advancedCurveEngine";

export type RepeatDoseKind =
  | "near-duplicate"
  | "overlap-likely"
  | "extension-likely"
  | "carryover-possible"
  | "separate-episode";

export type InferenceConfidence = "baixa" | "moderada" | "alta";

export interface RepeatDoseInput {
  id?: string;
  substance_name?: string | null;
  substanceName?: string | null;
  actual_time?: string | null;
  actualTime?: string | null;
  dose_amount?: number | null;
  doseAmount?: number | null;
  dose_unit?: string | null;
  formulation_name?: string | null;
  formulation?: string | null;
  total_duration_max_value?: number | null;
  total_duration_unit?: string | null;
  half_life_max_value?: number | null;
  half_life_unit?: string | null;
  substances?: Record<string, any> | null;
  substance_formulations?: Record<string, any> | null;
}

export interface RepeatDoseSignal {
  id: string;
  canonicalKey: string;
  substanceLabel: string;
  previousDoseId?: string;
  currentDoseId?: string;
  previousAt: string;
  currentAt: string;
  intervalHours: number;
  previousDoseAmount: number | null;
  currentDoseAmount: number | null;
  kind: RepeatDoseKind;
  label: string;
  explanation: string;
  possibleConsequences: string[];
  missing: string[];
  confidence: InferenceConfidence;
}

function actualTime(dose: RepeatDoseInput) {
  return dose.actual_time ?? dose.actualTime ?? null;
}

function substanceLabel(dose: RepeatDoseInput) {
  return dose.substance_name ?? dose.substanceName ?? dose.substances?.name ?? "substância";
}

function doseAmount(dose: RepeatDoseInput) {
  const value = dose.dose_amount ?? dose.doseAmount;
  if (value == null || !Number.isFinite(Number(value))) return null;
  return Number(value);
}

function toHours(value: unknown, unit: unknown): number | null {
  const numeric = Number(value);
  if (!Number.isFinite(numeric) || numeric <= 0) return null;
  const normalized = String(unit ?? "horas").toLowerCase();
  if (/min/.test(normalized)) return numeric / 60;
  if (/dia|day/.test(normalized)) return numeric * 24;
  if (/semana|week/.test(normalized)) return numeric * 24 * 7;
  return numeric;
}

function estimatedDurationHours(dose: RepeatDoseInput): number | null {
  const formulation = dose.substance_formulations ?? {};
  const substance = dose.substances ?? {};
  return (
    toHours(formulation.total_duration_max_value, formulation.total_duration_unit)
    ?? toHours(dose.total_duration_max_value, dose.total_duration_unit)
    ?? toHours(substance.total_duration_max_value, substance.total_duration_unit)
    ?? null
  );
}

function estimatedHalfLifeHours(dose: RepeatDoseInput): number | null {
  const formulation = dose.substance_formulations ?? {};
  const substance = dose.substances ?? {};
  return (
    toHours(formulation.half_life_max_value, formulation.half_life_unit)
    ?? toHours(dose.half_life_max_value, dose.half_life_unit)
    ?? toHours(substance.half_life_max_value, substance.half_life_unit)
    ?? null
  );
}

function isLisdexamfetamine(key: string) {
  return /lisdex|lyberdia|venvanse|vyvanse/.test(key);
}

function classifyPair(previous: RepeatDoseInput, current: RepeatDoseInput): RepeatDoseSignal | null {
  const previousAt = actualTime(previous);
  const currentAt = actualTime(current);
  if (!previousAt || !currentAt) return null;

  const previousMs = new Date(previousAt).getTime();
  const currentMs = new Date(currentAt).getTime();
  if (!Number.isFinite(previousMs) || !Number.isFinite(currentMs) || currentMs <= previousMs) return null;

  const label = substanceLabel(current);
  const canonicalKey = canonicalSubstanceKey(label);
  const previousKey = canonicalSubstanceKey(substanceLabel(previous));
  if (!canonicalKey || canonicalKey !== previousKey) return null;

  const intervalHours = (currentMs - previousMs) / 3_600_000;
  const duration = estimatedDurationHours(current) ?? estimatedDurationHours(previous);
  const halfLife = estimatedHalfLifeHours(current) ?? estimatedHalfLifeHours(previous);
  const previousAmount = doseAmount(previous);
  const currentAmount = doseAmount(current);
  const missing = [
    previousAmount == null || currentAmount == null ? "dose quantitativa" : null,
    !duration ? "duração da formulação" : null,
    !halfLife ? "meia-vida" : null,
    !(current.formulation_name || current.formulation || current.substance_formulations?.formulation_name) ? "formulação" : null,
  ].filter(Boolean) as string[];

  let kind: RepeatDoseKind;
  let signalLabel: string;
  let explanation: string;
  let possibleConsequences: string[];

  if (intervalHours <= 0.75) {
    kind = "near-duplicate";
    signalLabel = "Doses muito próximas";
    explanation = "As administrações estão próximas o suficiente para exigir confirmação de duplicidade de registro ou fracionamento intencional. Não é apropriado inferir potencialização apenas pelo horário.";
    possibleConsequences = ["maior carga total", "sobreposição de absorção", "maior incerteza sobre o pico observado"];
  } else if (isLisdexamfetamine(canonicalKey)) {
    if (intervalHours <= 3) {
      kind = "overlap-likely";
      signalLabel = "Sobreposição precoce de lisdexanfetamina";
      explanation = "A segunda dose ocorre enquanto a primeira ainda pode estar em absorção/conversão. O efeito esperado é sobreposição de exposição e aumento da carga total; a magnitude e o horário do pico não podem ser definidos apenas pelo intervalo.";
      possibleConsequences = ["exposição mais alta ou mais larga", "pico deslocado ou ampliado de modo variável", "maior carga autonômica e risco de efeitos adversos"];
    } else if (intervalHours <= 14) {
      kind = "extension-likely";
      signalLabel = "Prolongamento com sobreposição possível";
      explanation = "Duas doses de lisdexanfetamina no mesmo dia tendem a prolongar a janela de exposição e podem se sobrepor. Isso não deve ser rotulado automaticamente como potencialização: pico, intensidade e efeitos adversos dependem de dose, intervalo, alimentação, sono e variabilidade individual.";
      possibleConsequences = ["duração mais longa", "cauda mais tardia", "insônia ou ativação autonômica", "benefício e custo deslocados no tempo"];
    } else if (intervalHours <= 24) {
      kind = "carryover-possible";
      signalLabel = "Carry-over possível";
      explanation = "O intervalo é longo, mas ainda pode haver efeito residual ou metabólito ativo. A segunda administração deve ser interpretada como novo episódio com possível carry-over, não como soma linear nem potencialização certa.";
      possibleConsequences = ["efeito residual", "alteração do sono", "sobreposição pequena e incerta"];
    } else {
      kind = "separate-episode";
      signalLabel = "Episódio de dose separado";
      explanation = "O intervalo é compatível com administrações em dias distintos. Ainda pode existir carry-over individual, mas não há base temporal suficiente para classificar como sobreposição relevante sem dados observados.";
      possibleConsequences = ["variabilidade entre dias", "carry-over individual possível"];
    }
  } else {
    const likelyOverlapWindow = duration ?? Math.max(8, halfLife ?? 0);
    const possibleCarryoverWindow = Math.max(likelyOverlapWindow, halfLife ? halfLife * 2 : 24);
    if (intervalHours <= Math.min(3, likelyOverlapWindow * 0.35)) {
      kind = "overlap-likely";
      signalLabel = "Sobreposição precoce provável";
      explanation = "A segunda dose ocorre dentro da janela inicial estimada da primeira. A carga pode aumentar e a curva combinada pode mudar, mas não é possível afirmar potencialização ou um pico específico sem dados da formulação e resposta observada.";
      possibleConsequences = ["maior carga total", "mudança da forma da curva", "efeitos adversos dependentes de exposição"];
    } else if (intervalHours <= likelyOverlapWindow) {
      kind = "extension-likely";
      signalLabel = "Prolongamento/sobreposição provável";
      explanation = "O intervalo está dentro da duração estimada do efeito. A segunda dose pode prolongar a exposição e sobrepor parte da curva anterior; a consequência clínica depende de dose, formulação e contexto.";
      possibleConsequences = ["duração mais longa", "cauda tardia", "benefício ou adversidade em nova fase"];
    } else if (intervalHours <= possibleCarryoverWindow) {
      kind = "carryover-possible";
      signalLabel = "Carry-over possível";
      explanation = "A duração principal pode ter terminado, mas meia-vida ou efeito residual permitem carry-over. A associação deve ser validada com check-ins e curva observada.";
      possibleConsequences = ["efeito residual", "acúmulo possível em doses repetidas"];
    } else {
      kind = "separate-episode";
      signalLabel = "Episódio de dose separado";
      explanation = "O intervalo ultrapassa a janela estimada de efeito e carry-over. Trate como episódio separado, preservando a possibilidade de variabilidade individual.";
      possibleConsequences = ["variabilidade individual"];
    }
  }

  const confidence: InferenceConfidence = duration && previousAmount != null && currentAmount != null
    ? "alta"
    : duration || halfLife || isLisdexamfetamine(canonicalKey)
      ? "moderada"
      : "baixa";

  return {
    id: `${previous.id ?? previousAt}-${current.id ?? currentAt}`,
    canonicalKey,
    substanceLabel: label,
    previousDoseId: previous.id,
    currentDoseId: current.id,
    previousAt,
    currentAt,
    intervalHours,
    previousDoseAmount: previousAmount,
    currentDoseAmount: currentAmount,
    kind,
    label: signalLabel,
    explanation,
    possibleConsequences,
    missing,
    confidence,
  };
}

export function classifyRepeatDoses(doses: RepeatDoseInput[]): RepeatDoseSignal[] {
  const groups = new Map<string, RepeatDoseInput[]>();
  for (const dose of doses) {
    const time = actualTime(dose);
    const key = canonicalSubstanceKey(substanceLabel(dose));
    if (!time || !key) continue;
    const group = groups.get(key) ?? [];
    group.push(dose);
    groups.set(key, group);
  }

  const signals: RepeatDoseSignal[] = [];
  for (const group of groups.values()) {
    const sorted = [...group].sort((a, b) => new Date(actualTime(a) ?? 0).getTime() - new Date(actualTime(b) ?? 0).getTime());
    for (let index = 1; index < sorted.length; index += 1) {
      const signal = classifyPair(sorted[index - 1], sorted[index]);
      if (signal) signals.push(signal);
    }
  }
  return signals.sort((a, b) => new Date(b.currentAt).getTime() - new Date(a.currentAt).getTime());
}
